import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Users, UserPlus, ShieldCheck, ArrowLeft, Ban, Trash2, KeyRound } from "lucide-react";
import api from "../api";
import { getBrand } from "../utils/branding";

export default function TeamPage() {
  const brand = getBrand();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const S = {
    nav: { padding:"16px 32px", borderBottom:"1px solid var(--au-hair)", display:"flex", justifyContent:"space-between", alignItems:"center" },
    container: { maxWidth:900, margin:"0 auto", padding:"40px 24px" },
  };
  const fetchMembers = async () => {
    try { const r = await api.get("/api/agency/members"); setMembers(r.data.members||[]); }
    catch(e) { toast.error("Failed to load team"); }
    finally { setLoading(false); }
  };
  useEffect(()=>{ fetchMembers(); },[]);
  const invite = async () => {
    if (!form.name.trim()||!form.email.trim()) { toast.error("Name and email required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/api/agency/invite", form);
      toast.success(r.data.message||"Member invited!");
      if (r.data.temp_password) toast(`Temp password: ${r.data.temp_password}`, {duration:12000, icon:"🔑", style:{background:"var(--au-bg-elevated)",color:"var(--au-warn)",border:"1px solid var(--au-hair)"}});
      setShowInvite(false); setForm({name:"",email:""}); fetchMembers();
    } catch(e) { toast.error(e.response?.data?.detail||"Failed to invite"); }
    finally { setBusy(false); }
  };
  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from team?`)) return;
    try { await api.delete(`/api/agency/members/${id}`); toast.success(`${email} removed`); fetchMembers(); }
    catch(e) { toast.error(e.response?.data?.detail||"Failed"); }
  };
  return (
    <div className="au-root">
      <nav style={S.nav}>
        <Link to="/" className="au-brand" style={{textDecoration:"none"}}>
          <span className="au-mark"><ShieldCheck size={18} /></span>
          {brand.name||"AEGIS"}
        </Link>
        <Link to="/dashboard" style={{color:"var(--au-muted)",textDecoration:"none",fontSize:13,display:"inline-flex",alignItems:"center",gap:6}}><ArrowLeft size={14} /> Dashboard</Link>
      </nav>
      <div style={S.container}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:32,flexWrap:"wrap",gap:12}}>
          <div>
            <div className="au-eyebrow" style={{marginBottom:8}}>AGENCY PLAN · TEAM</div>
            <h1 style={{fontSize:36,fontWeight:800,margin:0}}>Your <span style={{color:"var(--au-accent-ink)"}}>Team</span></h1>
            <p style={{color:"var(--au-muted)",marginTop:8,fontSize:14}}>{members.length} member{members.length!==1?"s":""} · Full Aegis access, billing excluded</p>
          </div>
          <button onClick={()=>setShowInvite(true)} className="au-pill au-pill-primary"><UserPlus size={18} /> Invite Member</button>
        </div>
        {loading ? <div style={{color:"var(--au-muted)",textAlign:"center",padding:40}}>Loading...</div> :
         members.length===0 ? (
          <div className="au-glass" style={{padding:48,textAlign:"center"}}>
            <Users size={48} style={{color:"var(--au-accent-ink)",marginBottom:16}} />
            <div style={{color:"var(--au-text)",fontWeight:700,fontSize:18,marginBottom:8}}>No team members yet</div>
            <div style={{color:"var(--au-muted)",fontSize:14,marginBottom:24}}>Invite your team to collaborate on security scans</div>
            <button onClick={()=>setShowInvite(true)} className="au-pill au-pill-primary"><UserPlus size={18} /> Invite First Member</button>
          </div>
        ) : members.map(m=>(
          <div key={m.id} className="au-glass" style={{padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
            <div>
              <div style={{fontWeight:700,color:"var(--au-text)",fontSize:15}}>{m.name}</div>
              <div className="au-mono" style={{color:"var(--au-accent-ink)",fontSize:13,marginTop:2}}>{m.email}</div>
              <div style={{color:"var(--au-muted)",fontSize:11,marginTop:4}}>Joined {m.created_at} · {m.scans_used} scans</div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {m.is_banned && <span className="au-chip au-chip-status" style={{color:"var(--au-error)"}}><Ban size={14} /> BANNED</span>}
              <button onClick={()=>remove(m.id,m.email)} className="au-pill au-pill-glass au-pill-sm" style={{color:"var(--au-error)"}}><Trash2 size={14} /> Remove</button>
            </div>
          </div>
        ))}
      </div>
      {showInvite && (
        <div role="dialog" aria-modal="true" onClick={()=>setShowInvite(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:20}}>
          <div onClick={e=>e.stopPropagation()} className="au-glass au-pad-lg" style={{maxWidth:420,width:"100%"}}>
            <div style={{fontSize:18,fontWeight:700,color:"var(--au-text)",marginBottom:6,display:"flex",alignItems:"center",gap:8}}><UserPlus size={20} style={{color:"var(--au-accent-ink)"}} /> Invite Team Member</div>
            <div style={{fontSize:13,color:"var(--au-muted)",marginBottom:20}}>They'll receive login credentials by email (AGENCY plan access)</div>
            {[{k:"name",l:"Full Name",p:"e.g. Rahul Sharma"},{k:"email",l:"Email",p:"e.g. rahul@company.com"}].map(f=>(
              <div key={f.k} style={{marginBottom:16}}>
                <label style={{display:"block",fontSize:12,color:"var(--au-text-2)",marginBottom:6,fontWeight:600}}>{f.l}</label>
                <input value={form[f.k]} onChange={e=>setForm(s=>({...s,[f.k]:e.target.value}))} placeholder={f.p} className="au-input"/>
              </div>
            ))}
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20}}>
              <button onClick={()=>setShowInvite(false)} className="au-pill au-pill-glass au-pill-sm">Cancel</button>
              <button onClick={invite} disabled={busy} className="au-pill au-pill-primary au-pill-sm" style={{opacity:busy?0.6:1}}>{busy?"...":<><UserPlus size={14} /> Send Invite</>}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
